import { createStore } from '@aibees/shared/store';
import type { LookalikeSeedsStoreData as StoreData } from './types';

/** Upper bound on cached seed metadata per type, to keep localStorage small. */
const MAX_CACHED_SEEDS = 50;

/**
 * Caches lookalike seed metadata (label/logo) so the similarity search bar can
 * render chips without a browse round-trip. The URL `value` stays the canonical,
 * shareable source of truth for WHICH seeds exist; this only supplies the
 * metadata for them — see `useLookalikeSeeds`.
 *
 * Persisted so a hard refresh of the same search shows chips immediately. A
 * shared link opened by another user (cache miss) is hydrated once from the
 * backend and merged in.
 */
const useLookalikeSeedsStore = createStore<StoreData>(
  'lookalike-seeds',
  (set) => ({
    seedsByType: {},
    mergeSeeds: (type, seeds) =>
      set((state) => {
        const byValue = new Map(
          (state.seedsByType[type] ?? []).map((seed) => [seed.value, seed])
        );
        seeds.forEach((seed) => byValue.set(seed.value, seed));
        const merged = Array.from(byValue.values());
        state.seedsByType[type] = merged.slice(-MAX_CACHED_SEEDS);
      }),
    clearSeeds: (type) =>
      set((state) => {
        delete state.seedsByType[type];
      }),
  }),
  { persisted: true }
);

export default useLookalikeSeedsStore;
